import { useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Trash2, Sparkles, Droplet } from "lucide-react";

export default function ManualEmptyingDialog({ point, open, onClose, onSuccess }) {
  const [selectedContainers, setSelectedContainers] = useState([]);
  const [action, setAction] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const token = localStorage.getItem("token");

  const handleContainerToggle = (index) => {
    setSelectedContainers(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const handleSubmit = async () => {
    if (point.point_type === 'ressourceø' && selectedContainers.length === 0) {
      toast.error("Vælg mindst én container");
      return;
    }

    if ((point.point_type === 'toilet' || point.point_type === 'pissoir') && !action) {
      toast.error("Vælg en handling");
      return;
    }

    setSubmitting(true);
    try {
      const requestData = {
        point_id: point.id,
        request_type: point.point_type === 'ressourceø' ? 'tømning' : action,
        containers: point.point_type === 'ressourceø' 
          ? selectedContainers.map(idx => point.containers[idx])
          : [],
        source: "manual"
      };

      await axios.post(`${API}/emptying-requests`, requestData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Tømning bestilt manuelt!");
      setSelectedContainers([]);
      setAction(null);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error("Fejl ved bestilling");
    } finally {
      setSubmitting(false);
    }
  };

  if (!point) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-card">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold font-heading uppercase flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              point.status === 'grøn' ? 'bg-green-500' :
              point.status === 'gul' ? 'bg-yellow-500' :
              'bg-red-500'
            }`}>
              {point.point_type === 'ressourceø' ? (
                <Trash2 className="h-4 w-4 text-white" />
              ) : (
                <Sparkles className="h-4 w-4 text-white" />
              )}
            </div>
            {point.name}
          </DialogTitle>
          <p className="text-sm text-muted-foreground font-mono">
            {point.point_type} • Status: {point.status}
          </p>
        </DialogHeader>

        <div className="py-4">
          {point.status !== 'grøn' && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-3 mb-4">
              <p className="text-sm">
                {point.status === 'rød' && '⚠️ Tømning/rengøring er allerede bestilt'}
                {point.status === 'gul' && '🚛 Tømning/rengøring er under behandling'}
              </p>
            </div>
          )}

          {point.point_type === 'ressourceø' && (
            <div>
              <h3 className="text-sm font-bold font-heading uppercase mb-3">
                Vælg containere til tømning
              </h3>
              
              {point.containers && point.containers.length > 0 ? (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {point.containers.map((container, index) => (
                    <label
                      key={index}
                      className="flex items-start gap-3 p-3 bg-secondary/50 rounded-lg cursor-pointer hover:bg-secondary transition-colors"
                    >
                      <Checkbox
                        checked={selectedContainers.includes(index)}
                        onCheckedChange={() => handleContainerToggle(index)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <p className="font-bold font-mono text-sm">
                          {container.count}x {container.container_size}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {container.fraction_type}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Ingen containere på denne ø</p>
              )}
            </div>
          )}

          {(point.point_type === 'toilet' || point.point_type === 'pissoir') && (
            <div>
              <h3 className="text-sm font-bold font-heading uppercase mb-3">
                Vælg handling
              </h3>
              
              <div className="space-y-2">
                <button
                  onClick={() => setAction('gør_ren')}
                  className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                    action === 'gør_ren'
                      ? 'border-primary bg-primary/10'
                      : 'border-border bg-secondary/50 hover:bg-secondary'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Sparkles className="h-4 w-4" />
                    <div>
                      <p className="font-bold text-sm">Skal gøres ren</p>
                      <p className="text-xs text-muted-foreground">Bestil rengøring</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setAction('fyld_op')}
                  className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                    action === 'fyld_op'
                      ? 'border-primary bg-primary/10'
                      : 'border-border bg-secondary/50 hover:bg-secondary'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Droplet className="h-4 w-4" />
                    <div>
                      <p className="font-bold text-sm">Skal fyldes op</p>
                      <p className="text-xs text-muted-foreground">Bestil genopfyldning</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Annuller
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || point.status !== 'grøn'}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {submitting ? "Bestiller..." : "Bestil Manuelt"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
